import * as THREE from "three";
import { SkyShader } from "../shaders/SkyShader.js";
import { Lensflare, LensflareElement } from "../Lensflare.js";

export class Skybox extends THREE.Mesh {
  constructor(assets) {
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.ShaderMaterial({
      vertexShader: SkyShader.vertexShader,
      fragmentShader: SkyShader.fragmentShader,
      uniforms: {
        uSunAzimuth: { value: 216 },
        uSunElevation: { value: 24.68698059628387 },
        uSunColor: { value: new THREE.Color(0xffe5b0) },
        uSkyColorLow: { value: new THREE.Color(0x6fa2ef) },
        uSkyColorHigh: { value: new THREE.Color(0x2053ff) },
        uSunSize: { value: 1 }
      },
      side: THREE.BackSide,
      depthWrite: false,
      fog: false
    });

    super(geometry, material);

    this.SKYBOX_SCALE = 100000;
    this.distance = 0.5;
    this.sunElevation = 24.68698059628387;
    this.sunAzimuth = 216;
    this.targetElevation = this.sunElevation;
    this.targetAzimuth = this.sunAzimuth;
    this.lerpSpeed = 0.01;
    this._initialPositionSet = false;
    this._timeOfDay = null;

    this._sunPosition = new THREE.Vector3();
    this._sunDirection = new THREE.Vector3();
    this._lensflareScreenPos = new THREE.Vector3();
    this._tempProjectedDir = new THREE.Vector3();

    // Performance optimization: cache color objects and time
    this._lastUpdateTime = null;
    this._whiteColor = new THREE.Color(0xffffff);
    this._orangeColor = new THREE.Color(0xff4500);
    this._yellowColor = new THREE.Color(0xffd700);
    this._redColor = new THREE.Color(0xff6347);
    this._darkRedColor = new THREE.Color(0xd32f2f);
    this._skyBlueColor = new THREE.Color(0x87ceeb);
    this._darkSkyColor = new THREE.Color(0x0d1321);
    this._nightSkyColor = new THREE.Color(0x1c2331);
    this._moonColor = new THREE.Color(0xe6e8fa);
    this._tempHorizonColor = new THREE.Color();

    // DirectionalLight for realistic sun/moon lighting and shadows
    // Intensity varies by time of day (0.15-0.9)
    // Color changes from warm sunlight to cool moonlight
    this.sun = new THREE.DirectionalLight(0xffffff, 0.9);
    // Named so path-tracing.json's `keepRealtimeLights` can
    // keep the sun in the trace instead of replacing it with a rig light.
    this.sun.name = "sun";
    this.sun.castShadow = true;

    // 2048² is ample for a single 10×10m room; 4096² was needlessly heavy on the
    // GPU (it re-rendered every frame — see autoUpdate below).
    const shadowMapSize = 2048;

    this.sun.shadow.mapSize.set(shadowMapSize, shadowMapSize);
    this.sun.shadow.radius = 6; // Very soft, blurred shadows for smooth appearance

    // Shadow bias will be adjusted dynamically based on sun elevation
    // Low sun angles require different bias to prevent shadow acne
    this.sun.shadow.normalBias = 0.02;
    this.sun.shadow.bias = 0.0001;
    // The scene is static almost all the time, so don't re-render this (expensive)
    // shadow map every frame. It's refreshed on demand: while the sun moves (see
    // update() below) and when geometry changes (SceneManager.requestShadowUpdate).
    this.sun.shadow.autoUpdate = false;
    this.sun.shadow.radius = 8; // Very high blur to hide aliasing artifacts

    // Shadow camera bounds sized for 10x10x3.5m room
    // Very tight frustum for maximum shadow resolution
    const frustumSize = 7; // 10x10 coverage - exactly the room size
    this.sun.shadow.camera.left = -frustumSize;
    this.sun.shadow.camera.right = frustumSize;
    this.sun.shadow.camera.top = frustumSize;
    this.sun.shadow.camera.bottom = -frustumSize;
    this.sun.shadow.camera.near = 0.5;
    this.sun.shadow.camera.far = 50;
    this.sun.shadow.camera.updateProjectionMatrix();

    // sun is NOT added to skybox - it will be added to scene separately

    // Reduced ambient light - main illumination now comes from directional sun/moon light
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    this.add(this.ambientLight);

    // Lensflare setup
    this.textureFlare0 = assets.textureFlare0;
    this.textureFlare3 = assets.textureFlare3;

    this.lensflare = new Lensflare();
    this.addLensflare();
    this.add(this.lensflare);

    this.onTimeOfDayChanged = null;

    this.updateSunPosition(true);

    this.scale.setScalar(this.SKYBOX_SCALE);
  }

  addLensflare() {
    const color = new THREE.Color(0xffe5b0);
    this.lensflare.addElement(new LensflareElement(this.textureFlare0, 300, 0, color));
    this.lensflare.addElement(new LensflareElement(this.textureFlare3, 60, 0.6, color));
    this.lensflare.addElement(new LensflareElement(this.textureFlare3, 70, 0.7, color));
    this.lensflare.addElement(new LensflareElement(this.textureFlare3, 120, 0.9, color));
    this.lensflare.addElement(new LensflareElement(this.textureFlare3, 70, 1, color));
  }

  update(currentTime, elapsedTime, playerPosition, camera) {
    if (!(currentTime instanceof Date) || isNaN(currentTime)) {
      console.error("Invalid time");
      return;
    }

    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const timeInHours = hours + minutes / 60;

    // Check if time has changed
    const timeHasChanged = this._lastUpdateTime === null || Math.abs(timeInHours - this._lastUpdateTime) >= 1/60;

    // Check if sun/moon is still lerping to target position
    const isLerping = this.targetElevation !== undefined &&
                      this.targetAzimuth !== undefined &&
                      (Math.abs(this.sunElevation - this.targetElevation) > 0.01 ||
                       Math.abs(this.sunAzimuth - this.targetAzimuth) > 0.01);

    // Performance optimization: skip expensive updates if time hasn't changed AND lerp is complete
    if (!timeHasChanged && !isLerping) {
      this._projectLensflare(playerPosition, camera);
      return;
    }

    // The sun is repositioning this frame (time changed or still lerping), so its
    // cast-shadow map is now stale. autoUpdate is off, so request a one-frame refresh.
    if (this.sun.castShadow) this.sun.shadow.needsUpdate = true;

    // If time changed, update all sky parameters
    if (timeHasChanged) {
      this._lastUpdateTime = timeInHours;

      const SUNRISE = 6;
      const SUNSET = 21;
      const DARKNESS_START = 20.42;
      const DARKNESS_END = 6.58;
      const maxElevation = 42;

      // Use cached color objects instead of creating new ones
      const whiteColor = this._whiteColor;
      const orangeColor = this._orangeColor;
      const yellowColor = this._yellowColor;
      const redColor = this._redColor;
      const darkRedColor = this._darkRedColor;
      const skyBlueColor = this._skyBlueColor;
      const darkSkyColor = this._darkSkyColor;
      const nightSkyColor = this._nightSkyColor;
      const moonColor = this._moonColor;

      const isInDarkTransition =
        (timeInHours >= DARKNESS_START && timeInHours <= SUNSET) ||
        (timeInHours >= SUNRISE && timeInHours <= DARKNESS_END);

      const isDaytime = timeInHours >= SUNRISE && timeInHours <= SUNSET;
      const wasNighttime = this._timeOfDay === "Nighttime";

      let normalizedTime;
      if (isDaytime) {
        normalizedTime = (timeInHours - SUNRISE) / (SUNSET - SUNRISE);
      } else {
        const nightHour = timeInHours >= SUNSET ? timeInHours : timeInHours + 24;
        normalizedTime = (nightHour - SUNSET) / (24 - SUNSET + SUNRISE);
      }

      let sunElevation = Math.cos(Math.PI * (normalizedTime - 0.5)) * maxElevation - 5;
      const sunAzimuth = 180 + 180 * normalizedTime;

      let _timeOfDay = "Nighttime";
      if (isDaytime) {
        if (normalizedTime <= 0.25) _timeOfDay = "Sunrise";
        else if (normalizedTime <= 0.75) _timeOfDay = "Midday";
        else _timeOfDay = "Sunset";
      }

      const isNowNighttime = _timeOfDay === "Nighttime";
      let isInstantTransition = wasNighttime !== isNowNighttime;
      if (!this._initialPositionSet) {
        this._initialPositionSet = true;
        isInstantTransition = true;
      }

      if (isDaytime) {
        const normalizedElevation = Math.min(sunElevation / maxElevation, 1);
        const t = Math.pow(1 - normalizedElevation, 3);

        this.material.uniforms.uSunColor.value.lerpColors(whiteColor, orangeColor, t);

        // Use cached temp color instead of cloning
        this._tempHorizonColor.copy(skyBlueColor);
        if (_timeOfDay === "Sunrise") {
          this._tempHorizonColor.copy(yellowColor).lerp(redColor, normalizedTime / 0.25);
        } else if (_timeOfDay === "Sunset") {
          this._tempHorizonColor.copy(redColor).lerp(darkRedColor, (normalizedTime - 0.75) / 0.25);
        }

        this.material.uniforms.uSkyColorLow.value.copy(this._tempHorizonColor);
        this.material.uniforms.uSkyColorHigh.value.lerpColors(skyBlueColor, darkSkyColor, t);

        // DirectionalLight provides realistic sun illumination and shadows
        if (_timeOfDay === "Sunrise") {
          this.sun.intensity = 0.4;
          this.sun.color.setHex(0xffb380); // Warm orange morning light
        } else if (_timeOfDay === "Sunset") {
          this.sun.intensity = 0.45;
          this.sun.color.setHex(0xff8050); // Warm orange/red sunset light
        } else {
          // Midday - bright sunlight
          this.sun.intensity = 0.9;
          this.sun.color.setHex(0xfff4e0); // Bright warm white sunlight
        }

        // Adjust shadow bias based on sun elevation to prevent shadow acne at low angles
        // When sun is low (afternoon/sunset), increase bias to prevent artifacts
        const elevationFactor = Math.max(0, sunElevation) / maxElevation;
        this.sun.shadow.normalBias = 0.02 + (1 - elevationFactor) * 0.15; // Higher bias for low sun
        this.sun.shadow.bias = 0.0001 + (1 - elevationFactor) * 0.001;

        this.lensflare.visible = true;
      } else {
        sunElevation *= 0.5;
        this.material.uniforms.uSunColor.value.copy(moonColor).multiplyScalar(1.8);
        this.material.uniforms.uSkyColorLow.value.copy(darkSkyColor);
        this.material.uniforms.uSkyColorHigh.value.copy(nightSkyColor);

        // DirectionalLight provides subtle moonlight illumination and shadows
        this.sun.intensity = 0.15;
        this.sun.color.setHex(0xb3c5e6); // Cool blue moonlight

        // Adjust shadow bias for moonlight
        const elevationFactor = Math.max(0, Math.abs(sunElevation)) / (maxElevation * 0.5);
        this.sun.shadow.normalBias = 0.02 + (1 - elevationFactor) * 0.15;
        this.sun.shadow.bias = 0.0001 + (1 - elevationFactor) * 0.001;

        this.lensflare.visible = false;
      }

      this.targetElevation = sunElevation;
      this.targetAzimuth = sunAzimuth;
      this._isInstantTransition = isInstantTransition;

      if (_timeOfDay !== this._timeOfDay) {
        if (this.onTimeOfDayChanged) {
          this.onTimeOfDayChanged(_timeOfDay, elapsedTime);
        }
        this._timeOfDay = _timeOfDay;
      }
    } // End of if (timeHasChanged)

    // Always update sun position to continue lerp animation
    this.updateSunPosition(this._isInstantTransition || false);

    if (playerPosition) {
      const shadowDistance = 30; // Reduced from 300 for tighter, more accurate shadows
      // Set the LIGHT position (not shadow.camera.position)
      this.sun.position.copy(playerPosition).addScaledVector(this._sunDirection, shadowDistance);
      this.sun.target.position.copy(playerPosition);
      this.sun.target.updateMatrixWorld();
    }

    this._projectLensflare(playerPosition, camera);
  }

  // Runs every frame, not only when the sun moves: the flare tracks the camera too.
  _projectLensflare(playerPosition, camera) {
    if (playerPosition && camera) {
      const dir = this._sunDirection;

      this._tempProjectedDir
        .copy(camera.position)
        .addScaledVector(dir, 1000)
        .project(camera);

      this._lensflareScreenPos.copy(this._tempProjectedDir);

      this.lensflare._screenPositionOverridden = true;
      this.lensflare._overriddenScreenPosition = this._lensflareScreenPos;
    }
  }

  updateSunPosition(instant = false) {
    if (instant) {
      this.sunElevation = this.targetElevation;
      this.sunAzimuth = this.targetAzimuth;
    } else {
      this.sunElevation += (this.targetElevation - this.sunElevation) * this.lerpSpeed;
      this.sunAzimuth += (this.targetAzimuth - this.sunAzimuth) * this.lerpSpeed;
    }

    // Compass azimuth → yaw about +Y measured as atan2(x, z), with the viewer on +Z.
    const transformAzimuth = (oldAzimuth) => {
      return ((270 - oldAzimuth) % 360) - 90;
    };

    const el = THREE.MathUtils.degToRad(this.sunElevation);
    const az = THREE.MathUtils.degToRad(transformAzimuth(this.sunAzimuth));

    this._sunPosition.set(
      this.distance * Math.cos(el) * Math.sin(az),
      this.distance * Math.sin(el),
      this.distance * Math.cos(el) * Math.cos(az)
    );

    // Lensflare follows visual sun position (in skybox local space)
    this.lensflare.position.copy(this._sunPosition);

    this._sunDirection.copy(this._sunPosition).normalize();

    this.material.uniforms.uSunAzimuth.value = transformAzimuth(this.sunAzimuth);
    this.material.uniforms.uSunElevation.value = this.sunElevation;
  }

  sunDirection() {
    return this._sunDirection;
  }

  timeOfDay() {
    return this._timeOfDay;
  }
}
